/**
 * WebSocket Hook for Real-time Chat
 * Provides WebSocket connection with automatic reconnection.
 * HTTP polling remains as fallback - WebSocket is enhancement only.
 */
import { useEffect, useRef, useCallback, useState } from 'react';

const WS_RECONNECT_DELAY = 3000;
const WS_MAX_RECONNECT_ATTEMPTS = 5;

export const useChatWebSocket = (caseId, token, onMessage) => {
  const wsRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimeout = useRef(null);
  const [isConnected, setIsConnected] = useState(false);

  const connect = useCallback(() => {
    if (!caseId || !token) return;
    
    // Build WebSocket URL
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsHost = backendUrl.replace(/^https?:\/\//, '').replace(/\/api$/, '');
    const wsUrl = `${wsProtocol}//${wsHost}/ws/chat/${caseId}?token=${token}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('[WS] Connected to chat:', caseId);
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'pong') {
            // Heartbeat response
            return;
          }
          
          if (data.type === 'new_message' && onMessage) {
            onMessage(data);
          }
        } catch (e) {
          console.error('[WS] Parse error:', e);
        }
      };
      
      ws.onclose = (event) => {
        console.log('[WS] Disconnected:', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;
        
        // Attempt reconnect if not intentional close
        if (event.code !== 1000 && reconnectAttempts.current < WS_MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts.current++;
          console.log(`[WS] Reconnecting in ${WS_RECONNECT_DELAY}ms (attempt ${reconnectAttempts.current})`);
          reconnectTimeout.current = setTimeout(connect, WS_RECONNECT_DELAY);
        }
      };
      
      ws.onerror = (error) => {
        console.error('[WS] Error:', error);
      };
      
    } catch (error) {
      console.error('[WS] Connection error:', error);
    }
  }, [caseId, token, onMessage]);

  // Start heartbeat to keep connection alive
  useEffect(() => {
    if (!isConnected || !wsRef.current) return;
    
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Ping every 30 seconds
    
    return () => clearInterval(pingInterval);
  }, [isConnected]);

  // Connect when caseId/token changes
  useEffect(() => {
    connect();
    
    return () => {
      // Cleanup on unmount
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted');
      }
    };
  }, [connect]);

  // Disconnect function
  const disconnect = useCallback(() => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
    }
    setIsConnected(false);
  }, []);

  return {
    isConnected,
    disconnect,
    reconnect: connect
  };
};

export default useChatWebSocket;
